import { createColumnHelper, Row } from "@tanstack/react-table";
import { JSX } from "react";

import { ButtonTypes, CreateTableStyle, ListData_ValueTypes, SelectFieldOption_Values } from "../../../Definitions";
import { colors, glyphs, useUnitsComponent } from "../../../Styles";

import { EditAbleCell } from "../CellTypes/styledTableCell";
import { HeadCell } from "../CellTypes/headerTableCell";
import { StringTableCell } from "../CellTypes/stringTableCell";
import { NumberTableCell } from "../CellTypes/numberTableCell";
import { CheckboxTableCell } from "../CellTypes/checkboxTableCell";
import { ListTableCell } from "../CellTypes/listTableCell";
import { GenericColumnConstructorProps, GenericDataParameters, GenericRow } from "./useColumnConstructor";
import { DivTableCell } from "../CellTypes/divTableCell";
import { Button } from "../../BaseComponents";
import { ProgressTableCell } from "../CellTypes/progressTableCell";

export interface EditDataParameters extends GenericDataParameters {
  staticColumn?: boolean,
  options?: SelectFieldOption_Values[]
}

interface EditableColumnConstructorProps extends Omit<GenericColumnConstructorProps, "accessorData"> {
  accessorData: EditDataParameters[]
} 

export const useEditableColumnConstructor = <T extends GenericRow,> ({accessorData, tableStyle, canEdit}: EditableColumnConstructorProps & EditAbleCell ) => {
  const columnHelper = createColumnHelper<T>();
  const getUnit = useUnitsComponent();
  const {headCellStyle, bottomCellStyle} = CreateTableStyle(0, tableStyle)

  return accessorData.map(({accessor, unit, staticColumn=false, type, headerValue, idForColor, sort, options, width, divStyleOverride, styleOverrideOnValue}) => {
    const footerValue = unit ? getUnit(unit) : "";
    switch (type) {
      default: 
      case ListData_ValueTypes.String:
        return columnHelper.accessor(
          row => (row[accessor] as string) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => StringTableCell<T>({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {
              width: width,
             
            },
          }
        )
      case ListData_ValueTypes.List:
        return columnHelper.accessor(
          row => (row[accessor] as string) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => ListTableCell<T>({...props, canEdit, staticColumn, options: options ? options : []}),
            footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: {width: width}
          }
        )
      case ListData_ValueTypes.Number:
        return columnHelper.accessor(
          row => (row[accessor] as number) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => NumberTableCell({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width},
            sortDescFirst: false
          }
        )
      case ListData_ValueTypes.Check:
        return columnHelper.accessor(
        row => row[accessor] as boolean,
          {
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} colorId={idForColor} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: (props) => CheckboxTableCell({...props, canEdit, staticColumn}),
            footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={footerValue} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "basic" : undefined,
            meta: {width: width}
          }
        )
      case ListData_ValueTypes.Div:
        return columnHelper.accessor(
          row => (row[accessor] as JSX.Element) ?? "",
          {
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
            cell: props => DivTableCell({...props, canEdit, staticColumn, divStyleOverride, styleOverrideOnValue}),
            footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={""} keyPass={props.header.id+"footer"}/>),
            sortingFn: sort ? "alphanumeric" : undefined,
            meta: { 
              width: width,
             
            }
          }
        )
        case ListData_ValueTypes.ProgressBar:
          return columnHelper.accessor(
            row => (row[accessor] as JSX.Element) ?? "",
            {
              id: accessor,
              header: (props) => (<HeadCell {...props} sortArrow={sort} cellValue={headerValue} isSorted={props.column.getIsSorted()} key={props.header.id} style={headCellStyle} onClick={!canEdit && sort ? props.column.getToggleSortingHandler() : undefined} keyPass={props.header.id+"header"}/>),
              cell: props => ProgressTableCell({...props, canEdit, staticColumn}),
              footer: (props) => (<HeadCell {...props} key={props.header.id} style={bottomCellStyle} cellValue={""} keyPass={props.header.id+"footer"}/>),
              sortingFn: sort ? "alphanumeric" : undefined, // TODO: Sort on "remaining food"
              meta: {width: width}
            }
          )
      case ListData_ValueTypes.DeleteRowAction:
        const checkDeletePossible = (row: Row<T>, checkFunction?: (row: Row<T>) => boolean) => {
          if (checkFunction) {
            return checkFunction(row);
          }
          return true;
        }
        return columnHelper.display(
          { 
            id: accessor,
            header: (props) => (<HeadCell {...props} sortArrow={sort} key={"removeRowActionHeader"} keyPass="removeRowActionHeader" style={headCellStyle} cellValue={""}/>),
            cell: (props) => {return ((canEdit && checkDeletePossible(props.row, props.table.options.meta?.allowDeleteRowCheck)) ? (<Button content={""} onClick={() => props.table.options.meta?.deleteRow(props.row.original.id)} buttonType={ButtonTypes.textButton} startEnhancer={glyphs.Delete({fillColor: colors.greyDarkPrimary})}/>) :  (<div>{}</div>))},
            footer: (props) => (<HeadCell {...props} key={"removeRowActionFooter"} keyPass="removeRowActionFooter" style={bottomCellStyle} cellValue={""}/>),
            meta: {
              width: width,
            }
          }
        )
      }
    }
  )
}